import { useState, type FormEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/auth/AuthContext'
import { ApiError } from '@/api/client'

type Mode = 'field' | 'office'

/**
 * Sign in, with a mode for each population.
 *
 * Field  — employee number + PIN. Crews in gloves will not type an email
 *          address, and Sage holds no email addresses for them anyway.
 * Office — email + password. Desk users who can see every employee's hours
 *          and trigger exports to Sage need a proportionate credential.
 *
 * An explicit toggle rather than sniffing for '@' in one box: the two
 * populations are genuinely distinct, and guessing wrong on a supervisor's
 * first attempt is a bad first impression of the whole system.
 */
export function LoginPage() {
  const { signIn, signInWithPassword, isAuthenticated, hasRole } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [mode, setMode] = useState<Mode>('field')
  const [employeeNo, setEmployeeNo] = useState('')
  const [pin, setPin] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  if (isAuthenticated) return null

  function switchMode(next: Mode) {
    setMode(next)
    setError(null)
    setPin('')
    setPassword('')
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (mode === 'field') {
        await signIn(employeeNo, pin)
      } else {
        await signInWithPassword(email, password)
      }
      const from = (location.state as { from?: Location })?.from?.pathname
      navigate(from ?? (hasRole('Office') ? '/office' : '/field'), { replace: true })
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not sign in. Check your connection.',
      )
      setPin('')
      setPassword('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className={`center-screen ${mode === 'field' ? 'field-context' : ''}`}>
      <div className="signin">
        {/* No tagline. Anything describing today's feature set — time entry —
            will be wrong once scheduling, daily reports, change orders and
            cost forecasting land. "Job Management Portal" already carries the
            right breadth on its own. */}
        <div className="signin-brand">
          <span className="brand-mark">NW Linings</span>
          <span className="brand-sub">Job Management Portal</span>
          <div className="signin-rule" />
        </div>

        <form className="signin-card" onSubmit={handleSubmit}>
          <div className="segmented" role="tablist" aria-label="Sign in as">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'field'}
              onClick={() => switchMode('field')}
            >
              {/* "Field", not "Technician". This tab picks how you sign in —
                  employee number and PIN — and supervisors use it too. Naming
                  it after one role would read as excluding the other. */}
              Field
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'office'}
              onClick={() => switchMode('office')}
            >
              Office
            </button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

        {mode === 'field' ? (
          <>
            <div className="form-field">
              <label htmlFor="employeeNo">Employee number</label>
              <input
                id="employeeNo"
                name="employeeNo"
                value={employeeNo}
                onChange={(e) => setEmployeeNo(e.target.value)}
                autoComplete="username"
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                placeholder="F1505"
                required
                maxLength={7}
              />
            </div>
            <div className="form-field">
              <label htmlFor="pin">PIN</label>
              <input
                id="pin"
                name="pin"
                type="password"
                /* Raises the number pad rather than the full keyboard. */
                inputMode="numeric"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                autoComplete="current-password"
                required
                minLength={4}
                maxLength={12}
              />
            </div>
          </>
        ) : (
          <>
            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
          </>
        )}

          <button className="btn btn-block" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>

          {mode === 'office' && (
            <p
              className="muted"
              style={{ fontSize: '0.8125rem', marginTop: 'var(--sp-3)', marginBottom: 0 }}
            >
              Microsoft account sign-in is planned. For now, use the password
              set up for you.
            </p>
          )}
        </form>

        <p className="signin-foot">
          Works alongside Sage 100 — it does not replace it.
          <br />
          Trouble signing in? Contact the office.
        </p>
      </div>
    </div>
  )
}
